import React, { useState } from 'react';
import styled from 'styled-components';
import { PlacesType, Tooltip } from 'react-tooltip';
import { css } from '/imports/ui/css';
import { Icon, IconKey } from '/imports/ui/Icon';
import { defer, randomString } from '/imports/utils';
import { maxFieldLength } from '/imports/inputLimits';

export const InputStyled = styled.span`
  position: relative;
  input {
    background: ${css.inputBackgroundColor};
    border: ${css.inputBorder};
    border-radius: ${css.inputBorderRadius};
    padding: 0 2.5rem 0 1rem;
    min-height: ${css.inputHeight};
    box-sizing: border-box;
    font-size: 1rem;
    font-family: ${css.fontFamily};
    &::placeholder {
      font-style: italic;
    }
    &:focus {
      outline: ${css.inputFocusedBorder}
    }
    &:disabled {
      background-color: ${css.grey8};
      cursor: not-allowed;
    }
    &.has-start-icon {
      padding-left: 2.5rem;
    }
  }
  .clear {
    position: absolute;
    display: inline-flex;
    top: 0;
    right: 1rem;
    width: 1rem;
    height: 100%;
    cursor: pointer;
  }
  .icon-start {
    position: absolute;
    top: 1rem;
    left: 1rem;
  }
  .icon {
    position: absolute;
    display: inline-flex;
    top: 0;
    right: 1.25rem;
    width: 1rem;
    height: 100%;
    cursor: pointer;
    &.toggle-visibility {
      right: 3rem;
      width: 1rem;
    }
  }
  &.error {
    input {
      border-color: ${css.red2};
    }
  }
`;

export interface InputProps<T = string> {
  id?: string;
  inputId?: string;
  className?: string;
  type?: 'text' | 'password' | 'number' | 'color';
  placeholder?: string;
  value?: T;
  disabled?: boolean;
  autoFocus?: boolean;
  onChange?(value: T | undefined, e?: React.ChangeEvent<HTMLInputElement>): void;
  onFocus?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onBlur?(e: React.FocusEvent<HTMLInputElement, Element>): void;
  onClick?(e: React.MouseEvent<HTMLInputElement, MouseEvent>): void;
  onKeyPress?(e: React.KeyboardEvent<HTMLInputElement>): void;
  onEnter?(value: T | undefined, e?: React.KeyboardEvent<HTMLInputElement>): void;
  onClear?(): void;
  autoComplete?: 'on' | 'off';
  maxLength?: number;        // defaults to the stored-field ceiling, so the browser stops a paste the server would refuse
  icon?: IconKey;
  iconPlacement?: 'start' | 'end';
  hasError?: boolean;
  tip?: string;
  tipPlacement?: PlacesType;
  'data-tooltip-id'?: string;
  tipEvents?: {
    mouseenter?: boolean;
    focus?: boolean;
    mouseover?: boolean;
    click?: boolean;
    dblclick?: boolean;
    mousedown?: boolean;
  };
  required?: boolean;
}

/** Text/password/number/color input with optional icon, clear button, and tooltip. */
export const Input = (props: InputProps) => {
  const [type, setType] = useState(props.type || 'text');
  const [tipId,] = useState(props['data-tooltip-id'] || `tip${randomString()}`);
  /** Clears the value and fires the onClear callback. */
  const clearInput = () => {
    props.onChange?.('');
    defer(() => props.onClear?.());
  };
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  /** Toggles the input between password and plain text. */
  const togglePasswordVisibility = () => {
    setType(type === 'password' ? 'text' : 'password');
  };
  return (
    <InputStyled id={props.id} className={`input ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {hasStartIcon && <Icon className="icon-start" icon={props.icon as any} />}
      <input
        id={props.inputId}
        className={hasStartIcon ? 'has-start-icon' : ''}
        type={type || props.type || 'text'}
        placeholder={props.placeholder || ''}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        maxLength={props.maxLength ?? maxFieldLength}
        value={props.value || ''}
        onChange={(e) => props.onChange?.(e.target.value, e)}
        onClick={(e) => props.onClick?.(e)}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        onKeyUp={(e) => {
          props.onKeyPress?.(e);
          if (e.key === 'Enter') {
            defer(() => props.onEnter?.(props.value, e));
          }
        }}
        autoComplete={props.autoComplete}
        data-tooltip-id={tipId}
        required={props.required}
      />
      {
        props.value?.length &&
        !props.disabled &&
        !(props.type === 'color') &&
        <Icon className="clear" icon="cancel" onClick={() => clearInput()} /> || null
      }
      {props.value?.length && props.type === 'password' && <Icon className="toggle-visibility" icon={type === 'password' ? 'visibilityOff' : 'visibility'} onClick={() => togglePasswordVisibility()} /> || null}
      {
        props.tip ?
          <Tooltip id={tipId} content={props.tip} place={props.tipPlacement} openEvents={props.tipEvents || { mouseover: true }} />
          :
          null
      }
    </InputStyled>
  );
};